/*
 * NAMING THE SPECIALISTS.
 *
 * A specialist's id is a wire key — lowercase, hyphenated, stable, written into
 * every expert row and every audit entry. Its NAME is what a person reads. They
 * are different things, and the name is DECLARED rather than derived.
 *
 * The first attempt derived one from the other, which cannot be done without
 * guessing: nothing distinguishes `plc` from `plus`, so the rule needed a table
 * of acronyms — a second list to keep in step, and already wrong for the first
 * org that names a specialist after their own machine. The platform had settled
 * this before, in the permission catalog, where every key is stated in the proto
 * instead of being derived from its enum name.
 *
 * So: the thing that knows its name says it, and this file only decides what to
 * show when it hasn't.
 */
/**
 * The specialist every conversation starts with — the one people talk to.
 *
 * Named rather than represented by an empty string. Blank meant "no specialist",
 * which had to be explained everywhere it appeared and drew a second
 * "Conversation" row beside the real one: the same thing offered twice, once as
 * an absence and once as itself.
 */
export const DEFAULT_SPECIALIST = 'conversation';
/**
 * What to show for a specialist.
 *
 * Falls back to the ID, deliberately, and not to a prettified version of it. An
 * unnamed thing showing exactly what it is can be searched for, reported and
 * matched against the console; a guess at a nicer name cannot, and hides that
 * nobody has named it.
 */
export function specialistLabel(e) {
    return e.displayName?.trim() || e.id;
}
/**
 * WHICH SPECIALIST IS ACTUALLY ANSWERING — the one resolution, used by the picker
 * that displays it AND by the code that scopes tools to it.
 *
 * They used to be separate, and the gap between them was silent and total. The
 * picker showed "Conversation" whenever the roster had that row, while the
 * conversation's own `expertId` stayed EMPTY until somebody actively chose from
 * the dropdown — and an empty expert means `scopeToExpert` returns the FULL tool
 * union. So every thread anyone opened without touching the picker ran unscoped:
 * the conversational agent, whose subset lists seventeen tools and no memory
 * tools at all, was calling ai_admin_get_memory_graph, ai_admin_list_memory,
 * ai_admin_get_transcript and ai_admin_list_agent_audits — none of which it had
 * ever been given. Every tool_subset and every role decision behind it was inert
 * for the default case, which is nearly every case.
 *
 * Resolving in one place is what keeps the label and the reach the same claim.
 * An empty answer still means "no specialist, full union" — but now that only
 * happens on a surface whose roster genuinely has no default to fall back to,
 * which is the case the union exists for.
 */
export function effectiveSpecialist(chosen, roster) {
    if (chosen)
        return chosen;
    return roster.some((e) => e.id === DEFAULT_SPECIALIST) ? DEFAULT_SPECIALIST : '';
}
//# sourceMappingURL=specialists.js.map